import { useEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import clsx from 'clsx';
import { prefersReducedMotion } from '@/lib/dom';
import { play } from '@/lib/sound';
import { reboot } from '@/shell/Boot/reboot';
import { useWindowStore } from '@/wm/store';
import { useShutdownStore } from './store';
import css from './Shutdown.module.css';

/** How long the screen takes to go black. Matches the CSS transition. */
const FADE_MS = 1200;

/** Everything inside `root` that can hold the keyboard, in DOM order. */
function focusable(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  const all = root.querySelectorAll<HTMLElement>('button, input, select, textarea, [tabindex]');
  return Array.from(all).filter((el) => el.tabIndex >= 0 && !el.hasAttribute('disabled'));
}

/* --------------------------------------------------------------------------
   Artwork

   The panel's glyphs are drawn here rather than shipped as assets: they only
   exist inside this one dialog, and at 26px they are half a dozen paths each.
   -------------------------------------------------------------------------- */

function FlagMark() {
  return (
    <svg className={css.flag} viewBox="0 0 28 24" width={26} height={22} aria-hidden="true">
      {/* Four panes, sheared and tilted, the way the waving flag reads. */}
      <g transform="rotate(-7 14 12) skewY(6)">
        <rect x="2" y="3" width="11" height="8" rx="1.4" fill="#e8482c" />
        <rect x="15" y="3" width="11" height="8" rx="1.4" fill="#7cb900" />
        <rect x="2" y="13" width="11" height="8" rx="1.4" fill="#109ede" />
        <rect x="15" y="13" width="11" height="8" rx="1.4" fill="#fdc300" />
      </g>
    </svg>
  );
}

function PowerGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={24} height={24} aria-hidden="true" focusable="false">
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 3.6v8.2" />
        <path d="M7.1 6.6a7.2 7.2 0 1 0 9.8 0" />
      </g>
    </svg>
  );
}

function MoonGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={24} height={24} aria-hidden="true" focusable="false">
      <path
        d="M15.6 3.4a9 9 0 1 0 5 15.8A10.4 10.4 0 0 1 15.6 3.4z"
        fill="currentColor"
      />
    </svg>
  );
}

function RestartGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={24} height={24} aria-hidden="true" focusable="false">
      <path
        d="M12 4.6a7.4 7.4 0 1 0 7.1 9.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path d="M11.4 1.2l4.5 3.4-4.5 3.4z" fill="currentColor" />
    </svg>
  );
}

function SwitchUserGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={24} height={24} aria-hidden="true" focusable="false">
      <g fill="currentColor">
        <circle cx="8.4" cy="8.2" r="3.6" />
        <path d="M2.2 19.4c0-3.4 2.8-5.6 6.2-5.6s6.2 2.2 6.2 5.6z" />
        <circle cx="16.8" cy="7.4" r="2.9" opacity="0.75" />
        <path d="M12.6 17.2c.4-2.7 2.4-4.4 4.9-4.4 2.7 0 4.7 1.9 4.7 4.4z" opacity="0.75" />
      </g>
    </svg>
  );
}

function LogOffGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={24} height={24} aria-hidden="true" focusable="false">
      {/* A door, and the way out of it. */}
      <path
        d="M13.4 3.6H5.2v16.8h8.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M14 8.2l5.2 3.8-5.2 3.8v-2.6H9.6v-2.4H14z" fill="currentColor" />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */

type Tone = 'standby' | 'off' | 'restart' | 'switch' | 'logoff' | 'disabled';

const ORB_CLASS: Record<Tone, string> = {
  standby: css.orbStandby,
  off: css.orbOff,
  restart: css.orbRestart,
  switch: css.orbSwitch,
  logoff: css.orbLogoff,
  disabled: css.orbDisabled,
};

interface ChoiceProps {
  label: string;
  tone: Tone;
  glyph: ReactNode;
  disabled?: boolean;
  buttonRef?: RefObject<HTMLButtonElement | null>;
  onClick?: () => void;
}

/**
 * One round button and its caption. The caption is inside the button, which is
 * both what XP did — the whole tile was the hit target — and what gives the
 * control an accessible name without an `aria-label`.
 */
function Choice({ label, tone, glyph, disabled, buttonRef, onClick }: ChoiceProps) {
  return (
    <button
      type="button"
      ref={buttonRef}
      className={css.choice}
      disabled={disabled}
      onClick={onClick}
    >
      <span className={clsx(css.orb, ORB_CLASS[disabled ? 'disabled' : tone])} aria-hidden="true">
        {glyph}
      </span>
      <span className={css.choiceLabel}>{label}</span>
    </button>
  );
}

/* -------------------------------------------------------------------------- */

/** What the overlay is playing out, once a button has been pressed. */
type Ending = 'off' | 'reload';

/**
 * Turn Off Computer, and Log Off.
 *
 * Rendered at the very top of the app, above every window and the taskbar. The
 * store holds the phase so the Start menu can raise this from anywhere; the
 * local `ending` holds what happens after the fade, because logging off is a
 * reload with no phase of its own — the machine never actually stops.
 */
export function ShutdownOverlay() {
  const phase = useShutdownStore((s) => s.phase);
  const cancel = useShutdownStore((s) => s.cancel);

  const panelRef = useRef<HTMLDivElement>(null);
  const defaultRef = useRef<HTMLButtonElement>(null);

  // Sampled once: the overlay lives for a couple of seconds, so re-reading the
  // media query on change would only cause a pointless mid-fade restyle.
  const [reduced] = useState(prefersReducedMotion);
  const [ending, setEnding] = useState<Ending | null>(null);
  const [faded, setFaded] = useState(false);
  const [safe, setSafe] = useState(false);

  const showPanel = ending === null && (phase === 'shutdown' || phase === 'logoff');

  /* ------------------------------------------------------------- the panel */

  // Focus lands on the button XP marked as default, which also means every
  // subsequent Tab starts from inside the panel.
  useEffect(() => {
    if (!showPanel) return;
    defaultRef.current?.focus({ preventScroll: true });
  }, [showPanel, phase]);

  // Escape backs out; Tab is trapped. Capture phase so the windows underneath
  // — which have their own Escape handling — never see either key.
  useEffect(() => {
    if (!showPanel) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        cancel();
        return;
      }
      if (event.key !== 'Tab') return;

      const stops = focusable(panelRef.current);
      if (stops.length === 0) return;
      const active = document.activeElement;
      const first = stops[0];
      const last = stops[stops.length - 1];

      // Focus somewhere else entirely (a click landed on the backdrop, say):
      // pull it back in rather than letting Tab walk into the dead desktop.
      if (!panelRef.current?.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
        return;
      }
      if (active !== (event.shiftKey ? first : last)) return;
      event.preventDefault();
      (event.shiftKey ? last : first).focus({ preventScroll: true });
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [showPanel, cancel]);

  /* ------------------------------------------------------------- the ending */

  useEffect(() => {
    if (!ending) return;

    // A frame's grace so the transition has an opacity to travel from.
    const frame = requestAnimationFrame(() => setFaded(true));
    const timer = window.setTimeout(
      () => {
        if (ending === 'reload') reboot();
        else setSafe(true);
      },
      reduced ? 0 : FADE_MS,
    );

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [ending, reduced]);

  // The chime lives in the handlers rather than in the effect above: an event
  // handler runs exactly once per click, where a StrictMode effect would sound
  // it twice in development.
  const turnOff = () => {
    play('shutdown');
    useShutdownStore.getState().begin('off');
    setEnding('off');
  };

  const restart = () => {
    play('shutdown');
    useShutdownStore.getState().begin('restart');
    setEnding('reload');
  };

  /** Logging off has a chime of its own — it is not the shutdown sound. */
  const logOff = () => {
    play('logoff');
    // The reload wipes them anyway; closing them first means the desktop is
    // genuinely empty behind the fade rather than dissolving mid-session.
    useWindowStore.getState().closeAll();
    setEnding('reload');
  };

  if (phase === null) return null;

  const loggingOff = phase === 'logoff';

  return (
    <>
      {/* The backdrop stays up for the whole flow, so the desktop is still
          drained of colour underneath while the screen goes black. */}
      <div className={css.overlay}>
        {showPanel ? (
          <div
            ref={panelRef}
            className={clsx(css.panel, loggingOff && css.panelSmall)}
            role="dialog"
            aria-modal="true"
            aria-label={loggingOff ? 'Log Off Windows' : 'Turn off computer'}
          >
            <div className={css.panelHead}>
              <span className={css.panelTitle}>
                {loggingOff ? 'Log Off Windows' : 'Turn off computer'}
              </span>
              <FlagMark />
            </div>

            <div className={css.panelBody}>
              {loggingOff ? (
                <>
                  {/* One account on this machine, so there is nobody to switch to. */}
                  <Choice
                    label="Switch User"
                    tone="switch"
                    glyph={<SwitchUserGlyph />}
                    disabled
                  />
                  <Choice
                    label="Log Off"
                    tone="logoff"
                    glyph={<LogOffGlyph />}
                    buttonRef={defaultRef}
                    onClick={logOff}
                  />
                </>
              ) : (
                <>
                  {/* Stand By needs hardware to stand by. */}
                  <Choice label="Stand By" tone="standby" glyph={<MoonGlyph />} disabled />
                  <Choice
                    label="Turn Off"
                    tone="off"
                    glyph={<PowerGlyph />}
                    buttonRef={defaultRef}
                    onClick={turnOff}
                  />
                  <Choice
                    label="Restart"
                    tone="restart"
                    glyph={<RestartGlyph />}
                    onClick={restart}
                  />
                </>
              )}
            </div>

            <div className={css.panelFoot}>
              <button type="button" className={css.cancel} onClick={cancel}>
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {ending ? (
        <div className={clsx(css.blackout, faded && css.blackoutOn)}>
          {safe ? (
            <div className={css.safe} aria-live="polite">
              <div className={css.safeBar}>
                <p className={css.safeText}>It is now safe to turn off your computer.</p>
              </div>
              <button type="button" className={css.bootAgain} onClick={reboot}>
                Switch it back on
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
